if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        // Clean up event listener
        if (this.boundOptionChangeHandler) {
          const productInfo = this.closest('product-info');
          const productForm = productInfo?.querySelector('form[action*="/cart/add"]');
          if (productForm) {
            productForm.removeEventListener('change', this.boundOptionChangeHandler);
          }
          this.boundOptionChangeHandler = null;
        }

        // Clean up lightbox
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      init() {
        // theme.js populates FoxTheme.utils/config; on rare sessions it fails to
        // execute (stale cached bundle, vendor.js load failure, CSP). Bail without
        // throwing so the static gallery still renders. SHOPIFY-1V1 / SHOPIFY-1YV.
        if (!window.FoxTheme || !FoxTheme.utils || typeof FoxTheme.utils.queryDomNodes !== 'function') return;
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config?.mediaQueryMobile ?? 'screen and (max-width: 767px)');
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        // Add responsive media updates for immediate option changes
        this.initOptionChangeListener();
      }

      initOptionChangeListener() {
        // Find the product form within this gallery's product-info container
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const productForm = productInfo.querySelector('form[action*="/cart/add"]');
        if (!productForm) return;

        // Scope the listener to just this product's form
        this.boundOptionChangeHandler = (event) => {
          // Check if the change is from a variant option
          if (!event.target.matches('[name^="options["]')) return;

          const selectedOptions = {};

          // Collect all currently selected options
          productForm.querySelectorAll('[name^="options["]').forEach(input => {
            if (input.value) {
              const optionName = input.name.match(/options\[(.+)\]/)?.[1];
              if (optionName) {
                selectedOptions[optionName] = input.value;
              }
            }
          });

          // Try to find a variant that matches the current partial selection
          this.handlePartialOptionChange(selectedOptions);
        };

        productForm.addEventListener('change', this.boundOptionChangeHandler);
      }

      handlePartialOptionChange(selectedOptions) {
        // Get product data from the page
        const productData = this.getProductData();
        if (!productData || !productData.variants) return;

        // Find the first variant that matches all currently selected options
        const matchingVariant = productData.variants.find(variant => {
          return Object.entries(selectedOptions).every(([optionName, optionValue]) => {
            const optionIndex = productData.options?.findIndex(opt => opt.name === optionName);
            if (optionIndex === -1) return true;
            
            const variantOptionValue = variant.options?.[optionIndex];
            return variantOptionValue === optionValue;
          });
        });

        // If we found a matching variant, update the media immediately
        if (matchingVariant) {
          this.setActiveMedia(matchingVariant);
        }
      }

      getProductData() {
        // Try to get product data from various possible sources
        let productData = null;
        
        // Check for product JSON in script tags
        const productScript = document.querySelector('script[id*="ProductJSON"]');
        if (productScript) {
          try {
            productData = JSON.parse(productScript.textContent);
          } catch (_e) {
            // Failed to parse, continue to next method
          }
        }

        // Fallback: check for global product data
        if (!productData && window.product) {
          productData = window.product;
        }

        return productData;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          speed: 0,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          speed: 0,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            // Add navigation for vertical thumbnail carousel
            this.thumbsOptions.navigation = {
              nextEl: this.querySelector('.swiper-button--thumb-next'),
              prevEl: this.querySelector('.swiper-button--thumb-prev'),
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          // Sentry SHOPIFY-1MX: variant/media swaps can call initSlider()
          // before the Swiper vendor bundle has attached to FoxTheme.Swiper,
          // throwing "Cannot read properties of undefined (reading
          // 'Navigation')" from FoxTheme.Carousel's constructor. Same race
          // already guarded in collection-tiles-slider.js/
          // collection-list-carousel-v2.js/slideshow-component.js -- defer
          // via the one-shot FoxTheme:ready event instead of retrying here.
          if (typeof window.FoxTheme === 'undefined' || typeof FoxTheme.Swiper === 'undefined') {
            if (!this._awaitingFoxThemeReady) {
              this._awaitingFoxThemeReady = true;
              document.addEventListener(
                'FoxTheme:ready',
                () => {
                  this._awaitingFoxThemeReady = false;
                  this.initSlider();
                },
                { once: true }
              );
            }
            return;
          }

          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.guardSwiperLoopResize();
          this.initThumbnailClicks();
        }
      }

      // Guard against Swiper v8 race condition where updateSlidesClasses
      // throws "o.addClass is not a function" during ResizeObserver callbacks
      // in loop mode. The active slide index can go stale mid-resize, causing
      // Swiper's internal DOM7 .eq() to return an invalid object.
      // See: Sentry SHOPIFY-B3, GitHub #1083
      guardSwiperLoopResize() {
        const slider = this.sliderInstance?.slider;
        if (!slider || !slider.params?.loop) return;

        const orig = slider.updateSlidesClasses;
        slider.updateSlidesClasses = function () {
          try {
            orig.call(this);
          } catch {
            // Swallowed — classes reapply on next successful resize cycle
          }
        };
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        if (typeof window.FoxTheme?.PhotoSwipeLightbox !== 'function') {
          return;
        }
        const dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            // First check for PhotoSwipe JSON data script (for custom media)
            const pswpDataScript = media.querySelector('.pswp-item-data');
            if (pswpDataScript) {
              try {
                const pswpData = JSON.parse(pswpDataScript.textContent);
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: pswpData.src,
                  width: pswpData.width,
                  height: pswpData.height,
                  alt: pswpData.alt,
                  mediaType: media.dataset.mediaType || 'image',
                });
              } catch (error) {
                console.warn('Failed to parse PhotoSwipe data:', error);
              }
            } else {
              // Fallback to dataset attributes for standard media
              switch (media.dataset.mediaType) {
                case 'model':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('product-model').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'video':
                case 'external_video':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('video-element').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'image':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    src: media.dataset.src,
                    width: media.dataset.pswpWidth,
                    height: media.dataset.pswpHeight,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
              }
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id: _id }, _index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id: _id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          // iOS gesture-end rAF can fire 'change' after pswp.destroy() has nulled
          // lightbox.pswp — capture the reference at handler entry (not via the
          // `change` event since v5 doesn't pass pswp on this event) and re-check.
          const pswp = this.lightbox && this.lightbox.pswp;
          const slider = this.sliderInstance && this.sliderInstance.slider;
          if (!pswp || !slider) return;
          const { currIndex } = pswp;
          if (slider.realIndex !== currIndex) {
            slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          const currSlide = this.lightbox.pswp?.currSlide;
          if (currSlide && currSlide.data?.mediaType !== 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (_event, _el) => {
                this.lightbox.pswp?.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (_event, _el) => {
                this.lightbox.pswp?.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (_event, _el) => {
              this.lightbox.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      initThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        // Use event delegation - survives DOM replacement when custom media is loaded
        this.elements.thumbnails.addEventListener('click', (e) => {
          const thumbItem = e.target.closest('.product__thumbs-item');
          if (!thumbItem || !this.sliderInstance || !this.sliderInstance.slider) return;

          const slider = this.sliderInstance.slider;

          // Get the target media ID from the thumbnail
          const targetMediaId = thumbItem.dataset.target;

          if (targetMediaId) {
            // Find the actual slide element (including duplicates) and use slideTo with its index
            const allSlides = Array.from(slider.slides);
            const targetIndex = allSlides.findIndex(slide =>
              slide.dataset.mediaId === targetMediaId &&
              !slide.classList.contains('swiper-slide-duplicate')
            );

            if (targetIndex !== -1) {
              // Use slideTo with the actual DOM index, not slideToLoop
              slider.slideTo(targetIndex, 0);

              // Also scroll the thumbs carousel to show the clicked thumbnail
              if (this.thumbsInstance && this.thumbsInstance.slider) {
                const thumbSlides = Array.from(this.thumbsInstance.slider.slides);
                const thumbIndex = thumbSlides.indexOf(thumbItem);
                if (thumbIndex !== -1) {
                  this.thumbsInstance.slider.slideTo(thumbIndex, 0);
                }
              }
              return;
            }
          }

          // Fallback: use data-media-position (1-based) from thumbnail
          const mediaPosition = thumbItem.dataset.mediaPosition;
          if (mediaPosition) {
            const index = parseInt(mediaPosition, 10) - 1;
            if (index >= 0) {
              slider.slideToLoop(index, 0);

              // Also scroll thumbs carousel for fallback case
              if (this.thumbsInstance && this.thumbsInstance.slider) {
                this.thumbsInstance.slider.slideTo(index, 0);
              }
            }
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant, configMedia = null) {
        if (!variant) return;

        // Priority 1: Use caller-supplied media URL array (from variant.custom.media via storefrontMedia or custom-media-config)
        if (configMedia && Array.isArray(configMedia) && configMedia.length > 0) {
          this.updateGalleryFromUrls(configMedia, variant);
          return;
        }

        // Priority 2: Check for custom media from variant metafields (legacy approach)
        const customMediaConfig = this.getCustomMediaConfig(variant);
        if (customMediaConfig && customMediaConfig.hasCustomMedia) {
          this.updateCustomMedia(customMediaConfig);
          return;
        }

        // Priority 3: Fallback to standard featured media behavior
        if (!variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1, 0);
        } else {
          this.sortMediaItems(variant);
        }
      }

      /**
       * Show loading overlay on gallery and thumbnails
       * Uses lightbulb loader for brand consistency
       * @param {boolean} immediate - If true, show immediately without delay
       */
      showLoading(immediate = false) {
        // Clear any existing timer
        if (this.loadingTimer) {
          clearTimeout(this.loadingTimer);
          this.loadingTimer = null;
        }

        const showOverlays = () => {
          // Create loading overlay for main gallery if it doesn't exist
          if (!this.loadingOverlay) {
            this.loadingOverlay = document.createElement('div');
            this.loadingOverlay.className = 'media-gallery__loading';
            this.loadingOverlay.innerHTML = `
              <div class="lightbulb-loader" aria-hidden="true">
                <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                  <path class="bulb-glass" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M32 8 C18 8 12 20 12 28 C12 38 20 44 22 48 L42 48 C44 44 52 38 52 28 C52 20 46 8 32 8Z"/>
                  <path class="bulb-base" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M22 48 L22 54 L42 54 L42 48 M24 54 L24 58 L40 58 L40 54"/>
                  <path class="bulb-filament" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M28 36 L28 28 L32 32 L36 28 L36 36"/>
                </svg>
              </div>
            `;
          }

          // Add to gallery viewer
          const viewer = this.elements.viewer || this.querySelector('[id^="GalleryViewer"]');
          if (viewer && !viewer.contains(this.loadingOverlay)) {
            viewer.style.position = 'relative';
            viewer.appendChild(this.loadingOverlay);
          }

          // Defer the visibility flip to rAF so any sync layout reads (the
          // transition trigger) happen on the next frame, not the click frame.
          // The id is kept so hideLoading can cancel a flip that hasn't fired
          // yet — hide runs synchronously, and on warm caches it runs in the
          // same task as show, so an uncancelled add would land after the
          // remove and stick the overlay on permanently.
          const overlay = this.loadingOverlay;
          if (typeof window.requestAnimationFrame === 'function') {
            if (this.overlayRafId) window.cancelAnimationFrame(this.overlayRafId);
            this.overlayRafId = window.requestAnimationFrame(() => {
              this.overlayRafId = null;
              overlay.classList.add('is-visible');
            });
          } else {
            overlay.classList.add('is-visible');
          }

          // Also show loading on thumbnails
          if (this.elements.thumbnails) {
            // Create thumbnail loading overlay if it doesn't exist
            if (!this.thumbnailLoadingOverlay) {
              this.thumbnailLoadingOverlay = document.createElement('div');
              this.thumbnailLoadingOverlay.className = 'media-gallery__loading media-gallery__loading--thumbnails';
              this.thumbnailLoadingOverlay.innerHTML = `
                <div class="lightbulb-loader lightbulb-loader--small" aria-hidden="true">
                  <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                    <path class="bulb-glass" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M32 8 C18 8 12 20 12 28 C12 38 20 44 22 48 L42 48 C44 44 52 38 52 28 C52 20 46 8 32 8Z"/>
                    <path class="bulb-base" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M22 48 L22 54 L42 54 L42 48 M24 54 L24 58 L40 58 L40 54"/>
                    <path class="bulb-filament" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M28 36 L28 28 L32 32 L36 28 L36 36"/>
                  </svg>
                </div>
              `;
            }

            // Add to thumbnails container
            if (!this.elements.thumbnails.contains(this.thumbnailLoadingOverlay)) {
              this.elements.thumbnails.style.position = 'relative';
              this.elements.thumbnails.appendChild(this.thumbnailLoadingOverlay);
            }

            // Defer the visibility flip to rAF (see main overlay above).
            const thumbOverlay = this.thumbnailLoadingOverlay;
            if (typeof window.requestAnimationFrame === 'function') {
              if (this.thumbOverlayRafId) window.cancelAnimationFrame(this.thumbOverlayRafId);
              this.thumbOverlayRafId = window.requestAnimationFrame(() => {
                this.thumbOverlayRafId = null;
                thumbOverlay.classList.add('is-visible');
              });
            } else {
              thumbOverlay.classList.add('is-visible');
            }
          }
        };

        if (immediate) {
          // Show immediately for DOM-swapping operations
          showOverlays();
        } else {
          // Show after 200ms delay for non-destructive operations
          this.loadingTimer = setTimeout(showOverlays, 200);
        }
      }

      /**
       * Hide loading overlay from gallery and thumbnails
       */
      hideLoading() {
        // Clear timer if loading hasn't shown yet
        if (this.loadingTimer) {
          clearTimeout(this.loadingTimer);
          this.loadingTimer = null;
        }

        // Cancel deferred visibility flips still waiting on their frame,
        // so they can't re-show the overlay after this remove.
        if (this.overlayRafId) {
          window.cancelAnimationFrame(this.overlayRafId);
          this.overlayRafId = null;
        }
        if (this.thumbOverlayRafId) {
          window.cancelAnimationFrame(this.thumbOverlayRafId);
          this.thumbOverlayRafId = null;
        }

        if (this.loadingOverlay) {
          this.loadingOverlay.classList.remove('is-visible');
        }

        if (this.thumbnailLoadingOverlay) {
          this.thumbnailLoadingOverlay.classList.remove('is-visible');
        }
      }

      /**
       * Preload an image and return a promise
       * @param {string} url - Image URL to preload
       * @param {number} timeout - Timeout in ms (default: 5000)
       * @returns {Promise} - Resolves when image loads, rejects on error or timeout
       */
      preloadImage(url, timeout = 5000) {
        return Promise.race([
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Image preload timeout')), timeout)
          )
        ]);
      }

      /**
       * Cancel any pending gallery update requests
       * Called when a new variant is selected to prevent race conditions
       */
      cancelPendingGalleryUpdate() {
        // Increment request version to invalidate any pending callbacks
        this.galleryRequestVersion = (this.galleryRequestVersion || 0) + 1;
      }

      /**
       * Update gallery from an array of image URLs (sourced from variant.custom.media —
       * via Storefront API → storefrontMedia or server-rendered custom-media-config).
       * Preloads images before swapping DOM to prevent flash of empty content.
       * @param {Array<string>} imageUrls - Array of image URLs
       * @param {Object} variant - The variant object for context
       */
      updateGalleryFromUrls(imageUrls, variant) {
        if (!imageUrls || !imageUrls.length) return;

        // Cancel any pending gallery updates from previous variant selections
        this.cancelPendingGalleryUpdate();
        const thisRequestVersion = this.galleryRequestVersion;

        // Show loading state IMMEDIATELY (covers existing content)
        this.showLoading(true);

        // Filter valid URLs first
        const validUrls = imageUrls.filter(url => this.isValidImageUrl(url));
        if (!validUrls.length) {
          this.hideLoading();
          return;
        }

        // Get the optimized URL for preloading (first image, main size)
        const firstImageUrl = this.generateCloudflareUrl(validUrls[0], { width: 810 });
        const firstThumbUrl = this.generateCloudflareUrl(validUrls[0], { width: 140 });

        // Preload first main image and thumbnail before swapping DOM
        Promise.all([
          this.preloadImage(firstImageUrl).catch(() => null),
          this.preloadImage(firstThumbUrl).catch(() => null)
        ]).then(() => {
          // Check if this request is still current (user hasn't selected another variant)
          if (thisRequestVersion !== this.galleryRequestVersion) {
            // Abort - a newer request has been made
            return;
          }
          // Now that images are cached, build and swap DOM
          this.swapGalleryContent(validUrls, variant);
        }).catch(() => {
          // Check if this request is still current
          if (thisRequestVersion !== this.galleryRequestVersion) {
            return;
          }
          // Fallback: swap anyway if preload fails
          this.swapGalleryContent(validUrls, variant);
        });
      }

      /**
       * Swap gallery content with new images (called after preload)
       * @param {Array<string>} imageUrls - Validated image URLs
       * @param {Object} variant - The variant object for context
       */
      swapGalleryContent(imageUrls, variant) {
        // Performance: Use DocumentFragment to batch DOM operations
        const fragment = document.createDocumentFragment();
        const thumbFragment = document.createDocumentFragment();

        const totalImages = imageUrls.length;

        imageUrls.forEach((imageUrl, index) => {
          const mediaId = `config-media-${variant.sku}-${index}`;
          const imageAlt = variant.title || 'Product image';

          // Create main slide (pass totalImages for responsive sizing)
          const mediaItem = this.createMediaSlide(imageUrl, mediaId, imageAlt, index, totalImages);
          fragment.appendChild(mediaItem);

          // Create thumbnail
          const thumbItem = this.createThumbnailSlide(imageUrl, mediaId, imageAlt, index);
          thumbFragment.appendChild(thumbItem);
        });

        // Update main gallery
        this.elements.mediaList.innerHTML = '';
        this.elements.mediaList.appendChild(fragment);

        // Update thumbnails if they exist
        if (this.elements.thumbnails) {
          const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbnailList) {
            thumbnailList.innerHTML = '';
            thumbnailList.appendChild(thumbFragment);
          }
        }

        // Reinitialize PhotoSwipe if zoom is enabled
        if (this.enableImageZoom) {
          this.reinitializeImageZoom();
        }

        // Update sliders
        this.updateSliderForCustomMedia();

        // Hide loading after first image in DOM loads (should be instant since preloaded)
        const firstImage = this.elements.mediaList.querySelector('img');
        if (firstImage) {
          if (firstImage.complete) {
            this.hideLoading();
          } else {
            firstImage.addEventListener('load', () => this.hideLoading(), { once: true });
            // Fallback timeout in case image fails to load
            setTimeout(() => this.hideLoading(), 3000);
          }
        } else {
          this.hideLoading();
        }
      }

      /**
       * Create a main gallery slide element
       * @param {string} imageUrl - Image URL
       * @param {string} mediaId - Unique media ID
       * @param {string} imageAlt - Alt text
       * @param {number} index - Position index
       * @param {number} totalImages - Total number of images (for layout calculations)
       */
      createMediaSlide(imageUrl, mediaId, imageAlt, index, totalImages = 1) {
        const mediaItem = document.createElement('div');
        mediaItem.className = 'product__media-item swiper-slide';
        mediaItem.setAttribute('tabindex', '-1');
        mediaItem.dataset.mediaId = mediaId;
        mediaItem.dataset.mediaType = 'image';
        mediaItem.dataset.mediaIndex = index;
        mediaItem.dataset.src = imageUrl;
        mediaItem.dataset.pswpWidth = '1946';
        mediaItem.dataset.pswpHeight = '1946';

        const container = document.createElement('div');
        container.className = 'product__media-item-container relative flex overflow-hidden blocks-radius media-type-image';
        container.style.setProperty('--aspect-ratio', '1.0');

        const motionElement = document.createElement('motion-element');
        motionElement.setAttribute('data-motion', 'zoom-out-sm');
        motionElement.className = 'block product__media media-wrapper';

        // Calculate sizes based on layout
        const sizes = this.getResponsiveSizes(totalImages);

        const img = document.createElement('img');
        img.src = this.generateCloudflareUrl(imageUrl, { width: 810 });
        img.srcset = this.generateCloudflareSrcset(imageUrl);
        img.dataset.originalSrc = imageUrl;
        img.alt = this.sanitizeAttribute(imageAlt);
        img.width = '1946';
        img.height = '1946';
        img.loading = index < 2 ? 'eager' : 'lazy';
        img.fetchpriority = index < 2 ? 'high' : 'low';
        // Native lazy loading handles this via loading="lazy" attribute
        img.sizes = sizes;

        motionElement.appendChild(img);
        container.appendChild(motionElement);

        // Add zoom functionality if enabled
        if (this.enableImageZoom) {
          const zoomIcon = document.createElement('div');
          zoomIcon.className = 'product__media-icon btn btn--white btn--small btn--icon btn--icon-circle';
          zoomIcon.setAttribute('aria-hidden', 'true');
          zoomIcon.innerHTML = '<span class="btn__text flex items-center"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom icon--medium" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/><path d="M16.4434 16.4453L20.9997 21.0016"/></svg></span>';

          const zoomButton = document.createElement('button');
          zoomButton.className = 'product__media-zoom btn--inherit js-photoswipe--zoom';
          zoomButton.type = 'button';
          zoomButton.setAttribute('aria-haspopup', 'dialog');
          zoomButton.setAttribute('aria-label', `Open media ${index + 1}`);
          zoomButton.dataset.pswpWidth = '1946';
          zoomButton.dataset.pswpHeight = '1946';
          zoomButton.dataset.mediaId = mediaId;
          zoomButton.dataset.mediaType = 'image';
          zoomButton.dataset.mediaIndex = index;
          zoomButton.innerHTML = `<span class="visually-hidden">Open media ${index + 1}</span>`;

          const zoomQuality = window.FoxTheme?.cloudflareConfig?.zoomQuality || 90;
          const pswpDataScript = document.createElement('script');
          pswpDataScript.type = 'application/json';
          pswpDataScript.className = 'pswp-item-data';
          pswpDataScript.textContent = JSON.stringify({
            src: this.generateCloudflareUrl(imageUrl, { width: 1960, quality: zoomQuality }),
            width: 1946,
            height: 1946,
            alt: this.sanitizeAttribute(imageAlt)
          });

          container.appendChild(zoomIcon);
          container.appendChild(zoomButton);
          container.appendChild(pswpDataScript);
        }

        mediaItem.appendChild(container);
        return mediaItem;
      }

      /**
       * Get responsive image sizes based on current layout
       * @param {number} totalImages - Total number of images
       * @returns {string} - Sizes attribute value
       */
      getResponsiveSizes(totalImages) {
        const pageWidth = window.FoxTheme?.settings?.pageWidth || 1260;
        const mediaWidth = 0.5; // Default media column width

        // Determine columns based on layout
        let desktopColumns = 1;
        if (this.mediaLayout === 'columns' && totalImages > 1) {
          desktopColumns = 2;
        }

        // Mobile is always single column
        const mobileSizes = 'calc(100vw - 3rem)';

        // Desktop sizes depend on layout
        let desktopSizes;
        if (desktopColumns > 1) {
          // 2-column layout
          desktopSizes = `calc(((min(${pageWidth + 30}px, 100vw) * ${mediaWidth}) - 4.5rem - ${desktopColumns - 1}rem) / ${desktopColumns})`;
        } else {
          // Single column (carousel, stacked, grid-mix)
          desktopSizes = `calc(((min(${pageWidth + 30}px, 100vw) * ${mediaWidth}) - 4.5rem) / 1)`;
        }

        return `(max-width: 767px) ${mobileSizes}, ${desktopSizes}`;
      }

      /**
       * Create a thumbnail slide element
       */
      createThumbnailSlide(imageUrl, mediaId, imageAlt, index) {
        const thumbItem = document.createElement('div');
        thumbItem.className = 'product__thumbs-item swiper-slide';
        thumbItem.dataset.target = mediaId;
        thumbItem.dataset.mediaPosition = index + 1;

        const thumbContainer = document.createElement('div');
        thumbContainer.className = 'product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer';

        const thumbInner = document.createElement('div');
        thumbInner.className = 'product__thumbs-inner';
        thumbInner.style.setProperty('--aspect-ratio', '1');

        const thumbImg = document.createElement('img');
        thumbImg.src = this.generateCloudflareUrl(imageUrl, { width: 140 });
        thumbImg.dataset.originalSrc = imageUrl;
        thumbImg.alt = this.sanitizeAttribute(imageAlt);
        thumbImg.width = '140';
        thumbImg.height = '140';
        thumbImg.loading = 'lazy';
        // Native lazy loading handles this via loading="lazy" attribute

        thumbInner.appendChild(thumbImg);
        thumbContainer.appendChild(thumbInner);
        thumbItem.appendChild(thumbContainer);
        return thumbItem;
      }

      getCustomMediaConfig(variant) {
        if (!variant || !variant.id) return null;

        const configElement = document.getElementById(`custom-media-config-${variant.id}`);
        if (!configElement) return null;

        try {
          return JSON.parse(configElement.textContent);
        } catch (error) {
          console.warn('Failed to parse custom media config:', error);
          return null;
        }
      }

      updateCustomMedia(customMediaConfig) {
        if (!customMediaConfig || !customMediaConfig.hasCustomMedia) {
          return;
        }

        // Find the custom media container for this variant
        const customContainer = document.querySelector(`.custom-media-container[data-variant-id="${customMediaConfig.variantId}"]`);
        if (!customContainer) {
          return;
        }

        // Get the custom media items
        const customMediaItems = customContainer.querySelectorAll('.product__media-item');
        if (!customMediaItems.length) {
          return;
        }

        // Cancel any pending gallery updates from previous variant selections
        this.cancelPendingGalleryUpdate();
        const thisRequestVersion = this.galleryRequestVersion;

        // Show loading IMMEDIATELY (covers existing content)
        this.showLoading(true);

        // Collect first image URL for preloading
        const firstItem = customMediaItems[0];
        const firstImg = firstItem?.querySelector('img');
        const firstImageUrl = firstImg ? (firstImg.dataset.originalSrc || firstImg.src) : null;

        // Preload first image before swapping DOM
        const preloadPromise = firstImageUrl && this.isValidImageUrl(firstImageUrl)
          ? this.preloadImage(this.generateCloudflareUrl(firstImageUrl, { width: 810 })).catch(() => null)
          : Promise.resolve();

        preloadPromise.then(() => {
          // Check if this request is still current (user hasn't selected another variant)
          if (thisRequestVersion !== this.galleryRequestVersion) {
            // Abort - a newer request has been made
            return;
          }
          this.swapCustomMediaContent(customMediaConfig, customContainer, customMediaItems);
        });
      }

      /**
       * Swap custom media content (called after preload)
       */
      swapCustomMediaContent(customMediaConfig, customContainer, customMediaItems) {
        // Performance: Use DocumentFragment to batch DOM operations
        const fragment = document.createDocumentFragment();

        // Create properly structured custom media items that match native template
        customMediaItems.forEach((item, index) => {
          // Extract data from the source item
          const imgElement = item.querySelector('img');
          if (!imgElement) return;

          // Get the original URL (prefer data-original-src for external images)
          const imageUrl = imgElement.dataset.originalSrc || imgElement.src;
          const imageAlt = imgElement.alt || 'Product image';
          const mediaId = item.dataset.mediaId || `custom-${index}`;
          
          // SECURITY FIX: Validate external URLs before processing
          if (!this.isValidImageUrl(imageUrl)) {
            console.warn('Invalid or potentially unsafe image URL detected:', imageUrl);
            return; // Skip this media item
          }
          
          // Get aspect ratio following theme pattern: prefer data attributes, fallback to calculation, then default
          let aspectRatio = item.dataset.aspectRatio;
          let actualWidth = parseInt(item.dataset.imageWidth) || 1946;
          let actualHeight = parseInt(item.dataset.imageHeight) || 1946;
          
          if (!aspectRatio) {
            // Fallback to calculating from naturalWidth/height of loaded image
            actualWidth = imgElement.naturalWidth || imgElement.width || actualWidth;
            actualHeight = imgElement.naturalHeight || imgElement.height || actualHeight;
            aspectRatio = actualWidth / actualHeight;
          }
          
          // Ensure we have a valid ratio, default to 1.0 like theme does
          aspectRatio = parseFloat(aspectRatio) || 1.0;
          
          // Create complete media item structure matching native template
          const mediaItem = document.createElement('div');
          mediaItem.className = 'product__media-item swiper-slide';
          mediaItem.setAttribute('tabindex', '-1');
          mediaItem.dataset.mediaId = this.sanitizeAttribute(mediaId);
          mediaItem.dataset.mediaType = 'image';
          mediaItem.dataset.mediaIndex = index;
          mediaItem.dataset.src = imageUrl; // Already validated above
          mediaItem.dataset.pswpWidth = actualWidth.toString();
          mediaItem.dataset.pswpHeight = actualHeight.toString();
          
          // Create the container with proper aspect ratio
          const container = document.createElement('div');
          container.className = 'product__media-item-container relative flex overflow-hidden blocks-radius media-type-image';
          container.style.setProperty('--aspect-ratio', aspectRatio.toString());
          
          // Create motion element wrapper
          const motionElement = document.createElement('motion-element');
          motionElement.setAttribute('data-motion', 'zoom-out-sm');
          motionElement.className = 'block product__media media-wrapper';
          
          // Create the image with proper attributes (SECURITY: URL already validated)
          // Use Cloudflare image proxy if enabled
          const newImg = document.createElement('img');
          newImg.src = this.generateCloudflareUrl(imageUrl, { width: 810 });
          newImg.srcset = this.generateCloudflareSrcset(imageUrl);
          newImg.dataset.originalSrc = imageUrl; // Store original for reference
          newImg.alt = this.sanitizeAttribute(imageAlt); // SECURITY: Sanitize alt text
          newImg.width = actualWidth.toString();
          newImg.height = actualHeight.toString();
          newImg.loading = index < 2 ? 'eager' : 'lazy';
          newImg.fetchpriority = index < 2 ? 'high' : 'low';
          // Native lazy loading handles this via loading attribute
          newImg.sizes = '(max-width: 767px) calc(100vw - 3rem), calc(((min(1260px, 100vw) * 0.5) - 4.5rem) / 1)';
          
          // Assemble the structure
          motionElement.appendChild(newImg);
          container.appendChild(motionElement);
          
          // Add zoom functionality if enabled
          if (this.enableImageZoom) {
            // Create zoom icon
            const zoomIcon = document.createElement('div');
            zoomIcon.className = 'product__media-icon btn btn--white btn--small btn--icon btn--icon-circle';
            zoomIcon.setAttribute('aria-hidden', 'true');
            zoomIcon.innerHTML = '<span class="btn__text flex items-center"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom icon--medium" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/><path d="M16.4434 16.4453L20.9997 21.0016"/></svg></span>';
            
            // Create zoom button
            const zoomButton = document.createElement('button');
            zoomButton.className = 'product__media-zoom btn--inherit js-photoswipe--zoom';
            zoomButton.type = 'button';
            zoomButton.setAttribute('aria-haspopup', 'dialog');
            zoomButton.setAttribute('aria-label', `Open media ${index + 1}`);
            zoomButton.dataset.pswpWidth = '1946';
            zoomButton.dataset.pswpHeight = '1946';
            zoomButton.dataset.mediaId = mediaId;
            zoomButton.dataset.mediaType = 'image';
            zoomButton.dataset.mediaIndex = index;
            zoomButton.innerHTML = `<span class="visually-hidden">Open media ${index + 1}</span>`;
            
            // Create PhotoSwipe data script (SECURITY: Using validated/sanitized data)
            // Use high-quality zoom image via Cloudflare
            const zoomQuality = window.FoxTheme?.cloudflareConfig?.zoomQuality || 90;
            const pswpDataScript = document.createElement('script');
            pswpDataScript.type = 'application/json';
            pswpDataScript.className = 'pswp-item-data';
            pswpDataScript.textContent = JSON.stringify({
              src: this.generateCloudflareUrl(imageUrl, { width: 1960, quality: zoomQuality }),
              width: 1946,
              height: 1946,
              alt: this.sanitizeAttribute(imageAlt) // Sanitized alt text
            });
            
            container.appendChild(zoomIcon);
            container.appendChild(zoomButton);
            container.appendChild(pswpDataScript);
          }
          
          mediaItem.appendChild(container);
          fragment.appendChild(mediaItem);
        });

        // Single DOM operation: clear and append all items at once
        this.elements.mediaList.innerHTML = '';
        this.elements.mediaList.appendChild(fragment);

        // Update thumbnails if they exist
        this.updateThumbnailsForCustomMedia(customMediaConfig, customContainer);

        // Reinitialize PhotoSwipe for custom media if zoom is enabled
        if (this.enableImageZoom) {
          this.reinitializeImageZoom();
        }

        // Reinitialize or update the slider
        this.updateSliderForCustomMedia();

        // Hide loading after first image in DOM loads (should be instant since preloaded)
        const firstImage = this.elements.mediaList.querySelector('img');
        if (firstImage) {
          if (firstImage.complete) {
            this.hideLoading();
          } else {
            firstImage.addEventListener('load', () => this.hideLoading(), { once: true });
            // Fallback timeout in case image fails to load
            setTimeout(() => this.hideLoading(), 3000);
          }
        } else {
          this.hideLoading();
        }
      }

      updateThumbnailsForCustomMedia(customMediaConfig, customContainer) {
        if (!this.elements.thumbnails) return;

        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;

        // Get the custom media items
        const customMediaItems = customContainer.querySelectorAll('.product__media-item');
        if (!customMediaItems.length) return;

        // Performance: Use DocumentFragment to batch DOM operations
        const fragment = document.createDocumentFragment();

        customMediaItems.forEach((item, index) => {
          const imgElement = item.querySelector('img');
          if (!imgElement) return;

          const imageUrl = imgElement.dataset.originalSrc || imgElement.src;
          const imageAlt = imgElement.alt || 'Product image';
          const mediaId = item.dataset.mediaId || `custom-${index}`;

          // Validate URL
          if (!this.isValidImageUrl(imageUrl)) return;

          // Create thumbnail item matching the native template structure
          const thumbItem = document.createElement('div');
          thumbItem.className = 'product__thumbs-item swiper-slide';
          thumbItem.dataset.target = mediaId;
          thumbItem.dataset.mediaPosition = index + 1;

          const thumbContainer = document.createElement('div');
          thumbContainer.className = 'product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer';

          const thumbInner = document.createElement('div');
          thumbInner.className = 'product__thumbs-inner';
          thumbInner.style.setProperty('--aspect-ratio', '1');

          const thumbImg = document.createElement('img');
          thumbImg.src = this.generateCloudflareUrl(imageUrl, { width: 140 });
          thumbImg.dataset.originalSrc = imageUrl;
          thumbImg.alt = this.sanitizeAttribute(imageAlt);
          thumbImg.width = '140';
          thumbImg.height = '140';
          thumbImg.loading = 'lazy';
          // Native lazy loading handles this via loading="lazy" attribute

          thumbInner.appendChild(thumbImg);
          thumbContainer.appendChild(thumbInner);
          thumbItem.appendChild(thumbContainer);
          fragment.appendChild(thumbItem);
        });

        // Clear and update thumbnails
        thumbnailList.innerHTML = '';
        thumbnailList.appendChild(fragment);
      }


      updateSliderForCustomMedia() {
        // Update thumbs slider if it exists
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.update();
          this.thumbsInstance.slider.slideTo(0, 0);
        }

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slider = this.sliderInstance.slider;

          // For loop mode, we need to properly recreate the loop slides
          // when the DOM has been modified
          if (slider.params.loop) {
            // Destroy existing loop duplicates
            slider.loopDestroy();
            // Recreate loop with new slides
            slider.loopCreate();
          }

          // Update slides and pagination
          slider.update();

          // Go to first real slide (not a duplicate)
          // Use slideToLoop to properly handle loop mode's duplicate slides
          slider.slideToLoop(0, 0);

          // Force pagination update
          if (slider.pagination && slider.pagination.el) {
            slider.pagination.render();
            slider.pagination.update();
          }

          // Accessibility: Manage focus for dynamic content
          setTimeout(() => {
            const firstSlide = this.querySelector('.swiper-slide-active');
            if (firstSlide) {
              const focusableElement = firstSlide.querySelector('[tabindex="0"], img, button');
              if (focusableElement) {
                focusableElement.focus();
              }
            }
          }, 100);
        } else {
          // Initialize slider if not already active
          this.updateMediaLayout();
        }
      }

      reinitializeImageZoom() {
        // Comprehensive cleanup to prevent memory leaks
        if (this.lightbox) {
          // Remove all event listeners
          this.lightbox.off('change');
          this.lightbox.off('pointerDown'); 
          this.lightbox.off('uiRegister');
          
          // Destroy lightbox instance
          this.lightbox.destroy();
          this.lightbox = null;
        }

        // Reinitialize with current media items - don't remove existing PhotoSwipe data scripts
        // as they should be properly maintained by setupCustomMediaImage
        this.initImageZoom();
      }

      sortMediaItems(variant) {
        const newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId === variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      /**
       * SECURITY: Validate image URLs to prevent malicious content
       * @param {string} url - URL to validate
       * @returns {boolean} - True if URL is valid and safe
       */
      isValidImageUrl(url) {
        if (!url || typeof url !== 'string') {
          return false;
        }

        try {
          const urlObj = new URL(url);

          // Only allow HTTPS for external URLs, allow HTTP for localhost/dev
          const isSecure = urlObj.protocol === 'https:' ||
                          (urlObj.protocol === 'http:' &&
                           (urlObj.hostname === 'localhost' ||
                            urlObj.hostname.includes('.myshopify.com')));

          // Check for suspicious patterns - always block these
          const suspiciousPatterns = [
            /javascript:/i,
            /data:/i,
            /vbscript:/i,
            /file:/i,
            /ftp:/i
          ];

          const hasSuspiciousPattern = suspiciousPatterns.some(pattern =>
            pattern.test(url)
          );

          if (!isSecure || hasSuspiciousPattern) {
            return false;
          }

          // Check for valid image file extensions (optional for CDN URLs)
          const validExtensions = /\.(jpg|jpeg|png|gif|webp|svg|avif)($|\?)/i;
          const hasValidExtension = validExtensions.test(urlObj.pathname) || validExtensions.test(urlObj.href);

          // For HTTPS URLs without extensions, allow known CDN/asset patterns
          // This handles CDN URLs that serve images without file extensions
          const isKnownAssetPattern = urlObj.hostname.includes('product-assets') ||
                                       urlObj.hostname.includes('cdn.shopify') ||
                                       urlObj.hostname.includes('cloudflare') ||
                                       urlObj.pathname.includes('/cdn-cgi/image/');

          return hasValidExtension || isKnownAssetPattern;
        } catch (_error) {
          // Invalid URL
          return false;
        }
      }

      /**
       * SECURITY: Sanitize HTML attributes to prevent XSS
       * @param {string} value - Attribute value to sanitize
       * @returns {string} - Sanitized attribute value
       */
      sanitizeAttribute(value) {
        if (typeof value !== 'string') {
          return String(value);
        }

        // Remove potentially dangerous characters and patterns
        return value
          .replace(/[<>"'&]/g, '') // Remove HTML characters
          .replace(/javascript:/gi, '') // Remove javascript: protocol
          .replace(/data:/gi, '') // Remove data: protocol
          .replace(/vbscript:/gi, '') // Remove vbscript: protocol
          .substring(0, 200); // Limit length
      }

      /**
       * Generate Cloudflare Images transform URL
       * @param {string} url - Original image URL
       * @param {Object} options - Transform options
       * @returns {string} - Cloudflare transformed URL or original if disabled
       */
      generateCloudflareUrl(url, options = {}) {
        const config = window.FoxTheme?.cloudflareConfig;

        // Return original URL if Cloudflare is disabled or no config
        if (!config || !config.enabled || !url) {
          return url;
        }

        const {
          width,
          height,
          quality = config.quality || 80,
          format = config.format || 'auto',
          fit = 'scale-down'
        } = options;

        // Build options string
        const optionParts = [];
        if (width) optionParts.push(`width=${width}`);
        if (height) optionParts.push(`height=${height}`);
        optionParts.push(`quality=${quality}`);
        optionParts.push(`format=${format}`);
        optionParts.push(`fit=${fit}`);
        const optionsStr = optionParts.join(',');

        // Build final URL
        if (config.proxyDomain) {
          return `https://${config.proxyDomain}/cdn-cgi/image/${optionsStr}/${url}`;
        } else {
          try {
            const urlObj = new URL(url);
            return `${urlObj.origin}/cdn-cgi/image/${optionsStr}${urlObj.pathname}`;
          } catch (_e) {
            return url;
          }
        }
      }

      /**
       * Generate srcset for responsive images using Cloudflare
       * @param {string} url - Original image URL
       * @param {Array} widths - Array of widths for srcset
       * @returns {string} - Complete srcset string
       */
      generateCloudflareSrcset(url, widths = [400, 620, 690, 810, 980, 1240, 1380, 1620, 1960]) {
        return widths
          .map(w => `${this.generateCloudflareUrl(url, { width: w })} ${w}w`)
          .join(', ');
      }
    }
  );
}
